import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { useProfile } from '../contexts/ProfileContext';

// End-card buttons for Pixley Originals, drawn as the show's own props: two manila case files
// that land on The Case Crew's cork board, or two seed packets for The Corner Garden
// (`theme` from src/config/originalsOutros.js). The outro video leaves the right ~40% of the frame empty;
// these pop into that space in time with the outro, so the call-to-action stays tappable,
// trackable and changeable without re-rendering any episode.
//
// "Next case": the next episode's photo clipped to a case file, with a countdown ring that
//   auto-plays it a few seconds after the video ends.
// "Make your own story": a fresh, empty case file stamped NEW. It goes through the app's
//   existing grown-up check (ProfileContext.requireParentalVerification, a math challenge on
//   child profiles) because it leads into create / sign-up / purchase.

const NEXT_COUNTDOWN_SECONDS = 8;
const INK = '#123f4b';
const MANILA_FRONT_A = '#f8e2b0';
const MANILA_FRONT_B = '#eccb86';
const MANILA_BACK = '#d9a95e';
const TAB = { next: '#0e7c86', new: '#6c5ce7' };

const Paperclip = ({ size }) => (
  <svg width={size * 0.42} height={size} viewBox="0 0 14 34" style={{ position: 'absolute', top: -size * 0.28, left: '22%', zIndex: 3 }} aria-hidden="true">
    <path d="M4 26V7a3 3 0 0 1 6 0v19a5 5 0 0 1-10 0V9" fill="none" stroke="#8d97a3" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M4 26V7a3 3 0 0 1 6 0v19a5 5 0 0 1-10 0V9" fill="none" stroke="#fff" strokeOpacity=".55" strokeWidth=".8" strokeLinecap="round" />
  </svg>
);

const FolderShape = ({ variant }) => (
  <svg viewBox="0 0 300 108" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }} aria-hidden="true">
    <defs>
      <linearGradient id={`casefile-front-${variant}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={MANILA_FRONT_A} />
        <stop offset="1" stopColor={MANILA_FRONT_B} />
      </linearGradient>
    </defs>
    {/* back panel with its tab */}
    <path d="M10 14 Q10 6 18 6 H84 Q90 6 93 11 L97 18 H290 Q296 18 296 24 V100 Q296 106 290 106 H16 Q10 106 10 100 Z" fill={MANILA_BACK} />
    {/* coloured tab strip */}
    <path d="M22 9 H80 Q85 9 87 13 L89 17 H22 Z" fill={TAB[variant]} />
    {/* front panel */}
    <rect x="4" y="22" width="292" height="86" rx="9" fill={`url(#casefile-front-${variant})`} />
    <rect x="4.75" y="22.75" width="290.5" height="84.5" rx="8.4" fill="none" stroke="#8a5a1c" strokeOpacity=".22" strokeWidth="1.5" />
    <line x1="14" y1="30" x2="286" y2="30" stroke="#fff" strokeOpacity=".45" strokeWidth="1" />
  </svg>
);

// The "make your own" photo: a kid's crayon doodle that wiggles like it is about to come to
// life, with a pencil and twinkling sparkles. It is what Pixley does: drawings become cartoons.
const Sparkle = ({ x, y, r, delay }) => (
  <motion.path
    d={`M${x} ${y - r} Q${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y} Q${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r} Q${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y} Q${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r} Z`}
    fill="#f7b928"
    stroke="#fff"
    strokeWidth="0.8"
    style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
    animate={{ scale: [0.55, 1.15, 0.55], opacity: [0.5, 1, 0.5], rotate: [0, 20, 0] }}
    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut', delay }}
  />
);

const DoodleSlot = () => (
  <svg viewBox="0 0 100 78" style={{ width: '100%', height: '100%', display: 'block', borderRadius: '0.08em', background: '#fffaf0' }} aria-hidden="true">
    <defs>
      <filter id="casefile-crayon" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="1.6" />
      </filter>
    </defs>
    {/* faint notebook lines */}
    {[18, 32, 46, 60].map((y) => <line key={y} x1="4" x2="96" y1={y} y2={y} stroke="#9fc4e8" strokeOpacity=".35" strokeWidth=".8" />)}
    {/* the doodle creature */}
    <motion.g
      style={{ transformBox: 'fill-box', transformOrigin: '50% 90%' }}
      animate={{ rotate: [-4, 4, -4], y: [0, -1.5, 0] }}
      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      filter="url(#casefile-crayon)"
    >
      <path d="M50 14 L52 6 M52 6 l-3 -1 m3 1 l3 -1" stroke="#e8594f" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <path d="M31 44 C29 27 40 15 51 15 C63 15 72 26 70 43 C69 58 60 66 50 66 C39 66 32 58 31 44 Z" fill="#b8a8ff" fillOpacity=".55" stroke="#6c5ce7" strokeWidth="2.6" strokeLinejoin="round" />
      <circle cx="43.5" cy="37" r="3.4" fill="#fff" stroke="#123f4b" strokeWidth="1.4" />
      <circle cx="57.5" cy="37" r="3.4" fill="#fff" stroke="#123f4b" strokeWidth="1.4" />
      <circle cx="44.2" cy="37.6" r="1.5" fill="#123f4b" />
      <circle cx="58.2" cy="37.6" r="1.5" fill="#123f4b" />
      <path d="M44 48 Q50.5 55 57 48" stroke="#123f4b" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M31.5 46 L22 40 M69.5 46 L79 38" stroke="#6c5ce7" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M43 65 L41 72 M57 65 L59 72" stroke="#6c5ce7" strokeWidth="2.4" strokeLinecap="round" />
    </motion.g>
    {/* the pencil that drew it */}
    <g transform="rotate(-32 80 66)">
      <rect x="66" y="63" width="22" height="6" rx="1" fill="#f6c12c" stroke="#b88a12" strokeWidth=".6" />
      <rect x="86" y="63" width="4" height="6" fill="#f08aa0" />
      <path d="M66 63 L60 66 L66 69 Z" fill="#f3d6a8" stroke="#b88a12" strokeWidth=".5" />
      <path d="M60 66 L62.2 65 L62.2 67 Z" fill="#333" />
    </g>
    <Sparkle x={16} y={20} r={6} delay={0} />
    <Sparkle x={84} y={16} r={4.5} delay={0.5} />
    <Sparkle x={20} y={62} r={3.8} delay={1.0} />
  </svg>
);

const CaseFile = ({ variant, tab, title, subtitle, thumbUrl, countdown, onClick, delay, fontPx, ariaLabel }) => {
  const photo = fontPx * 4.6;
  const ringR = 15.5;
  const ringC = 2 * Math.PI * ringR;
  const pct = countdown === null ? 0 : (NEXT_COUNTDOWN_SECONDS - countdown) / NEXT_COUNTDOWN_SECONDS;
  return (
    <motion.button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      initial={{ opacity: 0, x: fontPx * 3, y: -fontPx, rotate: 7, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, y: 0, rotate: variant === 'next' ? -1.5 : 1.2, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 260, damping: 17, delay }}
      whileHover={{ y: -fontPx * 0.25, rotate: 0, scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '300 / 108',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: fontPx,
        filter: 'drop-shadow(0 0.5em 0.7em rgba(40,20,5,0.35))',
        textAlign: 'left',
      }}
    >
      <FolderShape variant={variant} />
      {/* tab label */}
      <span style={{ position: 'absolute', left: '7.6%', top: '8.4%', fontSize: '0.5em', fontWeight: 700, letterSpacing: '0.12em', color: '#fff', lineHeight: 1 }}>
        {tab || (variant === 'next' ? 'NEXT CASE' : 'NEW CASE')}
      </span>

      <div style={{ position: 'absolute', left: '5%', right: '5%', top: '30%', bottom: '10%', display: 'flex', alignItems: 'center', gap: '0.75em' }}>
        {/* photo / blank slot */}
        <div style={{ position: 'relative', flex: `0 0 ${photo}px`, height: photo * 0.78, transform: 'rotate(-4deg)' }}>
          {variant === 'next' ? (
            <>
              <div style={{ width: '100%', height: '100%', background: '#fff', padding: '0.18em', boxShadow: '0 0.15em 0.35em rgba(0,0,0,0.3)', borderRadius: '0.15em' }}>
                {thumbUrl ? (
                  <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.08em', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#bfe3e6,#8fc7cc)' }} />
                )}
              </div>
              <Paperclip size={fontPx * 2.1} />
            </>
          ) : (
            <>
              <div style={{ width: '100%', height: '100%', background: '#fff', padding: '0.18em', boxShadow: '0 0.15em 0.35em rgba(0,0,0,0.3)', borderRadius: '0.15em' }}>
                <DoodleSlot />
              </div>
              <Paperclip size={fontPx * 2.1} />
            </>
          )}
        </div>

        {/* words */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Fredoka', 'Inter', sans-serif", fontWeight: 700, color: INK, fontSize: '1.3em', lineHeight: 1.02 }}>{title}</div>
          {subtitle && (
            <div
              style={{
                marginTop: '0.3em',
                fontSize: '0.62em',
                fontWeight: 600,
                color: '#8a5f25',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>

        {/* play + countdown ring, or the NEW stamp */}
        {variant === 'next' ? (
          <svg viewBox="0 0 40 40" style={{ flex: '0 0 2.3em', height: '2.3em' }} aria-hidden="true">
            <circle cx="20" cy="20" r="18" fill={TAB.next} />
            <circle cx="20" cy="20" r={ringR} fill="none" stroke="#fff" strokeOpacity=".25" strokeWidth="3" />
            <circle
              cx="20" cy="20" r={ringR} fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={ringC} strokeDashoffset={ringC * (1 - pct)} transform="rotate(-90 20 20)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
            <path d="M16.5 13.5 L27 20 L16.5 26.5 Z" fill="#fff" />
          </svg>
        ) : (
          <div
            style={{
              flex: '0 0 auto',
              transform: 'rotate(-12deg)',
              border: '0.12em solid #d6453d',
              color: '#d6453d',
              borderRadius: '0.25em',
              padding: '0.08em 0.3em',
              fontSize: '0.72em',
              fontWeight: 800,
              letterSpacing: '0.1em',
              opacity: 0.85,
            }}
          >
            NEW
          </div>
        )}
      </div>
    </motion.button>
  );
};

// ── The Corner Garden: seed packets ─────────────────────────────────────────────────────────
// The packets are rendered artwork (public/assets/originals/buttons/, same 3D look as the show);
// only the live parts are drawn here: the next episode's picture (seen through the packet's
// window, which is cut out of the art), the words, and the countdown ring on the sunflower.
const GARDEN_INK = '#1f4a2a';
const PACKET_ART = {
  next: { src: '/assets/originals/buttons/garden-packet-next.webp', aspect: 1000 / 535, window: [0.142, 0.157, 0.442, 0.896], text: [0.49, 0.16, 0.86, 0.86], band: [0.012, 0.09] },
  new: { src: '/assets/originals/buttons/garden-packet-new.webp', aspect: 1000 / 502, text: [0.47, 0.16, 0.86, 0.86], band: [0.022, 0.09] },
};
const SUNFLOWER_PLAY = '/assets/originals/buttons/garden-sunflower-play.webp';

const SunflowerCountdown = ({ pct, style }) => {
  const r = 28.5;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'absolute', aspectRatio: '1', ...style }} aria-hidden="true">
      <motion.img
        src={SUNFLOWER_PLAY}
        alt=""
        draggable={false}
        animate={{ rotate: [0, 6, 0, -6, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{ width: '100%', height: '100%', display: 'block', filter: 'drop-shadow(0 0.18em 0.25em rgba(60,40,0,0.35))' }}
      />
      {/* countdown ring around the seed head */}
      <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
        <circle cx="50" cy="52.6" r={r} fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 50 52.6)"
          style={{ transition: 'stroke-dashoffset 1s linear', filter: 'drop-shadow(0 0 1.5px rgba(0,0,0,0.35))' }} />
      </svg>
    </div>
  );
};

const pctBox = ([x1, y1, x2, y2]) => ({ left: `${x1 * 100}%`, top: `${y1 * 100}%`, width: `${(x2 - x1) * 100}%`, height: `${(y2 - y1) * 100}%` });

const SeedPacket = ({ variant, tab, title, subtitle, thumbUrl, countdown, onClick, delay, fontPx, ariaLabel }) => {
  const art = PACKET_ART[variant];
  const pct = countdown === null ? 0 : (NEXT_COUNTDOWN_SECONDS - countdown) / NEXT_COUNTDOWN_SECONDS;
  const [wx1, wy1, wx2, wy2] = art.window || [0, 0, 0, 0];
  return (
    <motion.button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      initial={{ opacity: 0, y: fontPx * 3, rotate: variant === 'next' ? -5 : 5, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, rotate: variant === 'next' ? -1.2 : 1.2, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 220, damping: 15, delay }}
      whileHover={{ y: -fontPx * 0.35, rotate: 0, scale: 1.035 }}
      whileTap={{ scale: 0.96 }}
      style={{ position: 'relative', width: '100%', aspectRatio: `${art.aspect}`, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: fontPx, textAlign: 'left', filter: 'drop-shadow(0 0.45em 0.6em rgba(30,40,10,0.32))' }}
    >
      {/* the next episode's picture sits UNDER the art and shows through the packet window */}
      {art.window && (
        <div style={{ position: 'absolute', ...pctBox([wx1 - 0.006, wy1 - 0.01, wx2 + 0.006, wy2 + 0.01]), borderRadius: '0.6em', overflow: 'hidden', background: 'linear-gradient(135deg,#cdeec0,#8fd17a)' }}>
          {thumbUrl && <img src={thumbUrl} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </div>
      )}
      <img src={art.src} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />

      {/* label reading up the ribbon band */}
      <span style={{ position: 'absolute', left: `${art.band[0] * 100}%`, width: `${(art.band[1] - art.band[0]) * 100}%`, top: '12%', bottom: '4%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <span style={{ transform: 'rotate(-90deg)', whiteSpace: 'nowrap', fontSize: '0.5em', fontWeight: 700, letterSpacing: '0.16em', color: '#fff', textShadow: '0 1px 1px rgba(0,0,0,0.25)' }}>{tab}</span>
      </span>

      {/* words */}
      <div style={{ position: 'absolute', ...pctBox(art.text), display: 'flex', flexDirection: 'column', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ fontFamily: "'Fredoka', 'Inter', sans-serif", fontWeight: 700, color: GARDEN_INK, fontSize: '1.4em', lineHeight: 1.02, letterSpacing: '-0.01em' }}>{title}</div>
        {subtitle && (
          <div style={{ marginTop: '0.35em', fontSize: '0.64em', fontWeight: 600, color: '#8a6a2c', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {subtitle}
          </div>
        )}
      </div>

      {variant === 'next' ? (
        <SunflowerCountdown pct={pct} style={{ width: '15.5%', left: `${(wx2 - 0.135) * 100}%`, top: `${(wy2 - 0.31) * 100}%` }} />
      ) : (
        <motion.span
          animate={{ rotate: [9, 14, 9], scale: [1, 1.05, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', right: '-2%', top: '-7%', background: 'linear-gradient(180deg,#f06a4f,#d8432f)', color: '#fff', borderRadius: '999px', border: '0.14em solid #fffaf0', padding: '0.3em 0.7em', fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.08em', boxShadow: '0 0.18em 0.35em rgba(80,20,0,0.35)', whiteSpace: 'nowrap', pointerEvents: 'none' }}
        >
          PLANT IT!
        </motion.span>
      )}
    </motion.button>
  );
};

// Words per theme. Case files talk about cases; seed packets talk about episodes and planting.
const THEMES = {
  caseFile: {
    Button: CaseFile,
    nextTab: (hasNext) => (hasNext ? 'NEXT CASE' : 'THIS CASE'),
    nextTitle: (hasNext) => (hasNext ? 'Next case' : 'Watch again'),
    replaySubtitle: 'Play this case from the start',
    nextAria: 'Next case',
    newTab: 'NEW CASE',
    newSubtitle: 'Start a new case in Pixley',
  },
  seedPacket: {
    Button: SeedPacket,
    nextTab: (hasNext) => (hasNext ? 'NEXT EPISODE' : 'THIS EPISODE'),
    nextTitle: (hasNext) => (hasNext ? 'Next episode' : 'Watch again'),
    replaySubtitle: 'Grow this one again from the start',
    nextAria: 'Next episode',
    newTab: 'GROW YOUR OWN',
    newSubtitle: 'Plant a brand-new story in Pixley',
  },
};

const OriginalsOutroButtons = ({
  visible,
  ended,
  hasNext,
  onNext,
  onReplay,
  episodeId,
  showId,
  showName,
  nextEpisode = null, // { title, thumbnailUrl }
  currentThumbnailUrl = null, // this episode's photo, shown on "Watch again"
  frame = null, // rendered picture rect {left, top, width, height}; buttons sit in its right ~40%
  theme = 'caseFile', // 'caseFile' (The Case Crew) | 'seedPacket' (The Corner Garden)
}) => {
  const T = THEMES[theme] || THEMES.caseFile;
  const Btn = T.Button;
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { requireParentalVerification } = useProfile();
  const [countdown, setCountdown] = useState(null);
  const shownTrackedRef = useRef(false);
  const autoAdvancedRef = useRef(false);

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackEvent('originals_outro_buttons_shown', { episode_id: episodeId, show_id: showId, show_name: showName, has_next: hasNext });
    }
    if (!visible) shownTrackedRef.current = false;
  }, [visible, episodeId, showId, showName, hasNext, trackEvent]);

  // Countdown to the next episode once the video has actually ended.
  useEffect(() => {
    if (!ended || !hasNext) {
      setCountdown(null);
      return undefined;
    }
    autoAdvancedRef.current = false; // a fresh countdown for this ending
    setCountdown(NEXT_COUNTDOWN_SECONDS);
    const id = setInterval(() => {
      setCountdown((c) => (c === null || c <= 0 ? c : c - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [ended, hasNext]);

  useEffect(() => {
    if (ended && countdown === 0 && !autoAdvancedRef.current) {
      autoAdvancedRef.current = true;
      trackEvent('originals_outro_click', { button: 'next_case_auto', episode_id: episodeId, show_id: showId, show_name: showName });
      onNext && onNext();
    }
  }, [ended, countdown, onNext, episodeId, showId, showName, trackEvent]);

  const click = (button, fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent('originals_outro_click', { button, episode_id: episodeId, show_id: showId, show_name: showName });
    fn();
  };

  // Lay the case files out inside the outro's empty zone, scaled to the picture's rendered size.
  const fw = frame?.width || 0;
  const fontPx = frame ? Math.max(8, Math.min(24, fw * 0.0215)) : 16;
  const zoneStyle = frame
    ? { left: frame.left + frame.width * 0.575, top: frame.top + frame.height * 0.5, width: frame.width * 0.37, transform: 'translateY(-50%)', gap: fw * 0.03 }
    : { right: '7%', top: '50%', width: 'min(34%, 420px)', transform: 'translateY(-50%)', gap: 20 };

  return (
    <AnimatePresence>
      {visible && (
        <div className="absolute z-40 flex flex-col items-stretch" style={{ ...zoneStyle, fontFamily: "'Fredoka', 'Inter', sans-serif" }}>
          <Btn
            key="next"
            variant="next"
            tab={T.nextTab(hasNext)}
            title={T.nextTitle(hasNext)}
            subtitle={hasNext ? nextEpisode?.title || null : T.replaySubtitle}
            thumbUrl={hasNext ? nextEpisode?.thumbnailUrl : currentThumbnailUrl}
            countdown={countdown}
            fontPx={fontPx}
            delay={0}
            ariaLabel={hasNext ? `${T.nextAria}${nextEpisode?.title ? `: ${nextEpisode.title}` : ''}` : 'Watch again'}
            onClick={click(hasNext ? 'next_case' : 'watch_again', hasNext ? onNext : onReplay)}
          />
          <Btn
            key="new"
            variant="new"
            tab={T.newTab}
            title="Make your own story"
            subtitle={T.newSubtitle}
            countdown={null}
            fontPx={fontPx}
            delay={0.3}
            ariaLabel="Make your own story"
            onClick={click('make_your_own_story', () => {
              setCountdown(null); // stop auto-advancing while a grown-up takes over
              requireParentalVerification('make your own story', () => {
                trackEvent('originals_outro_click', { button: 'make_your_own_story_confirmed', episode_id: episodeId, show_id: showId, show_name: showName });
                navigate('/create');
              });
            })}
          />
        </div>
      )}
    </AnimatePresence>
  );
};

export default OriginalsOutroButtons;
